import React, { useEffect, useState } from 'react';
import { Typography, Spin, Alert, Card, Tabs } from 'antd';
import { UploadOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getApprovedDealers, getMyJobs, type Dealer, type BulkUploadJob } from '../../api/services/bulkUploadService';
import BulkUploadPage from '../../bulk-upload/BulkUploadPage';
import { Table, Tag } from 'antd';

const { Text } = Typography;

const statusColors: Record<string, string> = {
  PENDING: 'default',
  PROCESSING: 'processing',
  COMPLETED: 'success',
  PARTIAL: 'warning',
  FAILED: 'error',
};

const AdminBulkUpload: React.FC = () => {
  const { t } = useTranslation();
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [loadingDealers, setLoadingDealers] = useState(true);
  const [dealerError, setDealerError] = useState<string | null>(null);

  const [jobs, setJobs] = useState<BulkUploadJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await getApprovedDealers();
        setDealers(d);
      } catch {
        setDealerError(t('admin.bulk_upload_admin.msg_load_dealers_failed'));
      } finally {
        setLoadingDealers(false);
      }
    })();
  }, []);

  const loadJobs = async () => {
    setLoadingJobs(true);
    try {
      const j = await getMyJobs();
      setJobs(j);
    } catch {
      // ignore
    } finally {
      setLoadingJobs(false);
    }
  };

  const jobColumns = [
    { title: t('admin.bulk_upload_admin.col_job_id'), dataIndex: 'jobId', render: (v: string) => <Text code style={{ fontSize: 11 }}>{v.slice(0, 8)}…</Text> },
    {
      title: t('admin.bulk_upload_admin.col_status'),
      dataIndex: 'status',
      render: (s: string) => <Tag color={statusColors[s] || 'default'}>{s}</Tag>,
    },
    { title: t('admin.bulk_upload_admin.col_total'), dataIndex: 'totalRows', width: 70 },
    { title: t('admin.bulk_upload_admin.col_success'), dataIndex: 'successCount', width: 80, render: (v: number) => <Text style={{ color: '#3f8600' }}>{v}</Text> },
    { title: t('admin.bulk_upload_admin.col_failed'), dataIndex: 'failureCount', width: 70, render: (v: number) => <Text type={v > 0 ? 'danger' : undefined}>{v}</Text> },
    { title: t('admin.bulk_upload_admin.col_dealer'), dataIndex: 'targetDealerId', render: (id?: string) => {
      if (!id) return '—';
      const d = dealers.find(d => d.id === id);
      return d ? d.companyName : id.slice(0, 8);
    }},
    { title: t('admin.bulk_upload_admin.col_started'), dataIndex: 'startedAt', render: (v?: string) => v ? new Date(v).toLocaleString() : '—' },
    { title: t('admin.bulk_upload_admin.col_completed'), dataIndex: 'completedAt', render: (v?: string) => v ? new Date(v).toLocaleString() : '—' },
  ];

  return (
    <Tabs
      defaultActiveKey="upload"
      onChange={(key) => { if (key === 'history') loadJobs(); }}
      items={[
        {
          key: 'upload',
          label: <span><UploadOutlined /> {t('admin.bulk_upload_admin.tab_new')}</span>,
          children: (
            <>
              {dealerError && (
                <Alert type="error" message={dealerError} style={{ marginBottom: 16 }} />
              )}
              {loadingDealers ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">{t('admin.bulk_upload_admin.loading_dealers')}</Text>
                  </div>
                </div>
              ) : (
                <BulkUploadPage isAdmin dealers={dealers} />
              )}
            </>
          ),
        },
        {
          key: 'history',
          label: <span><UnorderedListOutlined /> {t('admin.bulk_upload_admin.tab_history')}</span>,
          children: (
            <Card>
              <Table
                dataSource={jobs}
                columns={jobColumns}
                rowKey="jobId"
                loading={loadingJobs}
                size="small"
                pagination={{ pageSize: 15 }}
                locale={{ emptyText: t('admin.bulk_upload_admin.empty_no_jobs') }}
              />
            </Card>
          ),
        },
      ]}
    />
  );
};

export default AdminBulkUpload;
